import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/use-toast'
import { supabase } from '@/lib/supabase'

const CheckEmail: React.FC = () => {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const email = localStorage.getItem('signup-email') || ''

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsVerifying(true)
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'email'
      })
      if (error) throw error

      const profile = JSON.parse(localStorage.getItem('signup-profile') || '{}');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('User not found after OTP verification');

      // Store display_name in auth metadata (unified approach for all users)
      if (profile.display_name) {
        await supabase.auth.updateUser({
          data: { display_name: profile.display_name }
        });
      }

      // Store in public.users (first_name/last_name are deprecated, set to null)
      // NOTE: We use display_name in auth metadata as the primary name field
      await supabase.from('users').upsert({
        id: user.id,
        email: user.email,
        first_name: null, // Deprecated - use auth.users.raw_user_meta_data.display_name
        last_name: null,  // Deprecated - use auth.users.raw_user_meta_data.display_name
        phone_number: profile.phone_number || null,
        role: 'owner',
        last_seen: new Date().toISOString(),
      });
      localStorage.removeItem('signup-profile');
      localStorage.removeItem('signup-email');

      const { data: onboardingStatus } = await supabase
        .from('users')
        .select('onboarding_completed')
        .eq('id', user.id)
        .single();

      navigate(onboardingStatus?.onboarding_completed ? '/home' : '/onboarding/tutorial');
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Invalid code',
        description: err.message
      })
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col p-6 pb-10 items-center justify-center">
      <h1 className="text-2xl font-bold mb-2 text-center">Check your email</h1>
      <p className="text-muted-foreground text-center mb-6 max-w-md">
        Enter the 6-digit code sent to your email.
      </p>
      <form onSubmit={handleVerify} className="w-full max-w-xs flex flex-col gap-4">
        <Input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="6-digit code"
          maxLength={6}
          required
        />
        <Button
          type="submit"
          className="w-full bg-lime text-black hover:bg-lime/90 py-4 rounded-xl"
          disabled={isVerifying}
        >
          {isVerifying ? 'Verifying...' : 'Verify'}
        </Button>
      </form>
    </div>
  )
}

export default CheckEmail
